import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalsComponent from '../../../components/stories/atoms/modals'
import { buttonVariantOptions } from '../../../components/stories/atoms/button/constants'
import { modalSizeOptions } from '../../../components/stories/atoms/modals/constants'
import {
  DescriptionContainer,
  DescriptionText,
  ModalContentContainer
} from '../styled-component'
import CheckboxComponent from '../../../components/stories/atoms/checkboxes'
import { iconSizeOptions } from '../../../components/stories/atoms/icons/icons-sizes'
import { Confirm } from '../../../components/stories/atoms/icons/essentials'
import { checkboxSizeOptions } from '../../../components/stories/atoms/checkboxes/constants'
import { skipRevisionChangeConfirmationKey } from '../constants'

const ChangeRevisionConfirmModal = ({
  onClose,
  onConfirm = () => {},
  setSelectedRevision = () => {},
  isLoading,
  currentRevision,
  newRevision,
  isOpen
}) => {
  const { formatMessage } = useIntl()
  const [isSkipConfirmRevision, setIsSkipConfirmRevision] = useState(
    !!localStorage.getItem(skipRevisionChangeConfirmationKey)
  )

  // handlers
  const handleConfirmChange = (e) => {
    onConfirm(newRevision)
    onClose(e)
  }

  const handleCancelChange = (e) => {
    onClose(e)
    setSelectedRevision([
      { id: currentRevision.id, name: currentRevision.planName }
    ])
  }

  const handleCheckSkipConfirmation = () => {
    const nextIsSkipConfirmRevision = !isSkipConfirmRevision
    setIsSkipConfirmRevision(nextIsSkipConfirmRevision)

    if (nextIsSkipConfirmRevision) {
      localStorage.setItem(skipRevisionChangeConfirmationKey, 'true')
    } else {
      localStorage.removeItem(skipRevisionChangeConfirmationKey)
    }
  }

  // effects

  // The modal is kept mounted between opens (only `isOpen` toggles its
  // visibility), so re-sync the checkbox with localStorage every time it
  // opens instead of relying on the initial useState value.
  useEffect(() => {
    if (isOpen) {
      setIsSkipConfirmRevision(
        !!localStorage.getItem(skipRevisionChangeConfirmationKey)
      )
    }
  }, [isOpen])

  const ModalContent = () => (
    <ModalContentContainer>
      {/*
        Both paragraphs live in one key now: the copy carries the second one
        inside a `<p>` tag, so the split into two messages became a single
        message with a rich-text tag.
      */}
      <DescriptionContainer>
        <DescriptionText>
          <FormattedMessage
            id="plans_dialog_change-revision_description"
            values={{
              revision: newRevision?.name,
              currentRevision: currentRevision?.planName,
              p: (chunks) => (
                <DescriptionText as="span">{chunks}</DescriptionText>
              )
            }}
          />
        </DescriptionText>
      </DescriptionContainer>
      <CheckboxComponent.Root
        checked={isSkipConfirmRevision}
        onCheckedChange={handleCheckSkipConfirmation}
        label={formatMessage({ id: 'common_confirmations_skip-next-time' })}
        size={checkboxSizeOptions.SMALL}
      >
        <CheckboxComponent.Indicator>
          <Confirm size={iconSizeOptions['ICON-S']} />
        </CheckboxComponent.Indicator>
      </CheckboxComponent.Root>
    </ModalContentContainer>
  )

  return (
    <ModalsComponent
      isOpen={isOpen}
      onClose={handleCancelChange}
      title={formatMessage({ id: 'plans_dialog_change-revision_title' })}
      size={modalSizeOptions['X-SMALL']}
      showCloseButton={false}
      rightButtons={[
        {
          variant: buttonVariantOptions['TERTIARY-HOLLOW'],
          onClick: handleCancelChange,
          title: formatMessage({ id: 'cancel' }),
          disabled: isLoading
        },
        {
          variant: buttonVariantOptions.PRIMARY,
          onClick: handleConfirmChange,
          title: formatMessage({ id: 'change' }),
          loading: isLoading
        }
      ]}
      style={{
        zIndex: 2
      }}
    >
      <ModalContent />
    </ModalsComponent>
  )
}

ChangeRevisionConfirmModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  currentRevision: PropTypes.object,
  newRevision: PropTypes.object,
  isOpen: PropTypes.bool.isRequired
}

export default ChangeRevisionConfirmModal
